import { Box, Modal, Paper } from '@mui/material';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { makeStyles } from 'tss-react/mui';
import palette from '@ui/palette';
import Typography from '@ui/Typography';
import { usePromptsContext } from 'context/prompts/PromptsContext';
import isBrowser from '../../util/isBrowser';
import {
  DebugCommand,
  filterDebugCommands,
  resolveCommandLabel,
} from './commandRegistry';
import DebugHelper from './DebugHelper';
import registerDefaultDebugCommands from './registerDefaultDebugCommands';

const useStyles = makeStyles()(theme => ({
  modal: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    paddingTop: '15vh',
  },
  paper: {
    width: 'min(520px, calc(100vw - 32px))',
    backgroundColor: palette.gray_1000,
    border: `1px solid ${palette.gray_700}`,
    borderRadius: theme.spacing(1),
    overflow: 'hidden',
    outline: 'none',
  },
  search: {
    width: '100%',
    border: 'none',
    outline: 'none',
    background: 'transparent',
    color: theme.palette.common.white,
    fontSize: 15,
    lineHeight: 1.4,
    padding: theme.spacing(2),
    fontFamily: 'inherit',
    borderBottom: `1px solid ${palette.gray_700}`,
    '&::placeholder': {
      color: palette.gray_300,
      opacity: 1,
    },
  },
  list: {
    maxHeight: 320,
    overflowY: 'auto',
    padding: theme.spacing(0.75),
  },
  item: {
    display: 'block',
    width: '100%',
    textAlign: 'left',
    border: 'none',
    borderRadius: theme.spacing(0.5),
    background: 'transparent',
    color: theme.palette.common.white,
    cursor: 'pointer',
    padding: theme.spacing(1.25, 1.5),
    fontSize: 13,
    fontFamily: 'inherit',
    '&:hover, &[data-active="true"]': {
      backgroundColor: palette.gray_800,
    },
  },
  empty: {
    padding: theme.spacing(2),
  },
  hint: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1.5),
    borderTop: `1px solid ${palette.gray_700}`,
  },
  kbd: {
    fontSize: 10,
    fontFamily:
      'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
    color: palette.gray_300,
    border: `1px solid ${palette.gray_700}`,
    borderRadius: 3,
    padding: '1px 5px',
  },
}));

function isTextEntryKeyTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  if (target.isContentEditable) {
    return true;
  }
  if (
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement
  ) {
    return true;
  }
  if (target instanceof HTMLInputElement) {
    const { type } = target;
    if (
      type === 'button' ||
      type === 'checkbox' ||
      type === 'radio' ||
      type === 'submit' ||
      type === 'reset' ||
      type === 'file' ||
      type === 'color' ||
      type === 'range' ||
      type === 'hidden'
    ) {
      return false;
    }
    return true;
  }
  return false;
}

/**
 * ⌘K / Ctrl+K command palette for users with the debug feature.
 * Mount inside `<Debug>` so it only evaluates for permitted users.
 */
export default function Command() {
  const { classes } = useStyles();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const [, promptsDispatch] = usePromptsContext();

  const results = useMemo(() => {
    return open ? filterDebugCommands(query) : [];
  }, [query, open]);
  const safeActiveIndex =
    results.length === 0 ? 0 : Math.min(activeIndex, results.length - 1);

  useEffect(() => {
    registerDefaultDebugCommands(promptsDispatch);
  }, [promptsDispatch]);

  const close = useCallback(() => {
    setOpen(false);
    setQuery('');
    setActiveIndex(0);
  }, []);

  const openPalette = useCallback(() => {
    setQuery('');
    setActiveIndex(0);
    setOpen(true);
  }, []);

  const runCommand = useCallback(
    (command: DebugCommand) => {
      close();
      Promise.resolve(command.perform()).catch((error: unknown) => {
        // eslint-disable-next-line no-console
        console.error('[debug] command failed', command.id, error);
      });
    },
    [close]
  );

  useEffect(() => {
    if (!isBrowser) {
      return undefined;
    }

    const onKeyDown = (e: KeyboardEvent) => {
      const isModK =
        (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k' && !e.altKey;
      if (!isModK) {
        return;
      }
      // Allow ⌘K while the palette search is focused; otherwise skip text fields.
      if (!open && isTextEntryKeyTarget(e.target)) {
        return;
      }
      e.preventDefault();
      if (open) {
        close();
      } else {
        openPalette();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, close, openPalette]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const id = window.requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
    return () => window.cancelAnimationFrame(id);
  }, [open]);

  const onSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(i =>
        results.length === 0 ? 0 : (i + 1) % results.length
      );
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(i =>
        results.length === 0 ? 0 : (i - 1 + results.length) % results.length
      );
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const command = results[safeActiveIndex];
      if (command) {
        runCommand(command);
      }
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
    }
  };

  if (!isBrowser) {
    return null;
  }

  return (
    <>
      <DebugHelper />
      <Modal
        open={open}
        onClose={close}
        className={classes.modal}
        disableAutoFocus
        disableEnforceFocus
      >
        <Paper
          elevation={12}
          className={classes.paper}
          role="dialog"
          aria-label="Debug commands"
        >
          <input
            ref={inputRef}
            className={classes.search}
            value={query}
            onChange={e => {
              setQuery(e.target.value);
              setActiveIndex(0);
            }}
            onKeyDown={onSearchKeyDown}
            placeholder="Search debug commands…"
            aria-label="Search debug commands"
            autoComplete="off"
            spellCheck={false}
          />
          <Box className={classes.list} role="listbox">
            {results.length === 0 ? (
              <Typography
                variant="pSm"
                color="secondary"
                className={classes.empty}
                component="p"
              >
                No matching commands
              </Typography>
            ) : (
              results.map((command, index) => (
                <button
                  key={command.id}
                  type="button"
                  role="option"
                  aria-selected={index === safeActiveIndex}
                  data-active={index === safeActiveIndex}
                  className={classes.item}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => runCommand(command)}
                >
                  {resolveCommandLabel(command)}
                </button>
              ))
            )}
          </Box>
          <Box className={classes.hint}>
            <span className={classes.kbd}>↑↓</span>
            <span className={classes.kbd}>↵</span>
            <span className={classes.kbd}>esc</span>
          </Box>
        </Paper>
      </Modal>
    </>
  );
}
